import { until } from "@vueuse/core";

import { defineStore, storeToRefs } from "pinia";
import { computed, watch } from "vue";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import mixpanel from "@/lib/mixpanel";
import { type types, useClient, useServices } from "@/lib/openapi";
// Import enum directly as index only exports as types.
import { ClusterRole as ClusterRoleEnum } from "@/lib/openapi/gen/types.gen";
import { useOrganizationsManager } from "@/stores/organizations-store";

import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "./authentication-store";

export type MeUser = types.MeUser;
export type ClusterRole = types.ClusterRole;
export type UserPermissions = types.UserPermissions;
export type OrganizationCapabilities = types.OrganizationCapabilities;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const ClusterRole = ClusterRoleEnum;

export const useUserStore = defineStore("user", () => {
    const { organization, isLoadingOrganizations, showSelectOrganization } =
        useOrganizationManager();
    const { organizations } = useOrganizationsManager();
    const { signOut } = useAuthenticationStore();
    const { authStatus, authUser } = useAuthenticationManager();
    const router = useRouter();
    const route = useRoute();
    const user = ref<MeUser>();
    const currentOrganizationRoles = ref<ClusterRole[]>([]);
    const currentOrganizationPermissions = ref<UserPermissions>();
    const currentOrganizationCapabilities = ref<OrganizationCapabilities>();
    const controller = ref(new AbortController());

    const api = useServices({
        opts: {
            throwOnError: false,
        },
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });

    const preferencesClient = useClient({
        requires: { auth: true, organization: false, stek: false },
    });

    const updatePhotoUrlFromAuthData = async () => {
        try {
            await api.updateSelfUserMePut({
                body: {
                    profile_picture: authUser.value.photoUrl,
                },
            });
        } catch (error) {
            console.error(
                "Failed to update profile picture in backend:",
                error
            );
        }
    };

    async function updateUser() {
        controller.value.abort();
        controller.value = new AbortController();

        try {
            const { response, data } = await api.getSelfUserMeGet({
                signal: controller.value.signal,
            });
            if (!response) {
                // Request was aborted or failed before getting a response
                // This is expected during org/auth transitions - silently return
                return;
            } else if (!response.ok) {
                if (response.status === 401) {
                    router.push({ name: "home" });
                    await signOut({
                        severity: "error",
                        summary: "Incorrect login.",
                    });
                    reset();
                } else {
                    throw new Error(
                        response.statusText || "Failed to get user info"
                    );
                }
            } else {
                user.value = data;

                if (
                    authUser.value?.photoUrl &&
                    authUser.value.photoUrl !== user.value?.profile_picture
                ) {
                    updatePhotoUrlFromAuthData();
                }
                for (const [orgId, roles] of Object.entries(
                    user.value!.roles
                )) {
                    if (orgId === organization.value?.identity) {
                        currentOrganizationRoles.value = roles as ClusterRole[];
                        currentOrganizationPermissions.value =
                            user.value!.permissions[orgId];
                        currentOrganizationCapabilities.value =
                            user.value!.capabilities?.[orgId];
                        break;
                    }
                }
                mixpanel.identify(user.value?.identity);
                mixpanel.peopleSetOnce({
                    "role": currentOrganizationRole.value,
                });
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        }
    }

    function reset() {
        user.value = undefined;
    }

    // When the organization changes, the permissions and capabilities for the
    // current user change as well (the backend resolves per-org capabilities like
    // search/threads from each org's persistence mode).
    watch(
        () => organization.value?.identity,
        () => {
            updateUser();
        }
    );

    // Boot fetch. On org-scoped routes the organization watcher above fires
    // the (org-scoped) fetch as soon as the org resolves, so fetching again
    // here would just duplicate /user/me with a missing Organization header.
    // Only fetch org-less when this navigation genuinely won't resolve an
    // org: an org-less route, the org picker, or a failed org resolution.
    const bootUserFetch = async () => {
        try {
            await router.isReady();
            if (route.meta.requiresOrg) {
                await until(
                    () =>
                        organization.value !== undefined ||
                        showSelectOrganization.value ||
                        !route.meta.requiresOrg ||
                        (organizations.value !== undefined &&
                            !isLoadingOrganizations.value)
                ).toBeTruthy();
            }
        } catch {
            return;
        }
        if (
            authStatus.value === AuthStatus.SIGNED_IN &&
            !organization.value &&
            !user.value
        ) {
            updateUser();
        }
    };

    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus !== AuthStatus.SIGNED_IN) {
                reset();
            } else {
                bootUserFetch();
            }
        },
        { immediate: true }
    );

    // Backward-compat computed for callers that need a single role value (e.g. Mixpanel).
    const currentOrganizationRole = computed(
        () => currentOrganizationRoles.value[0]
    );

    // Search works in every persistence mode (VFS-native at VFS_FIRST, SQL ILIKE otherwise), so
    // it is no longer a capability and the UI always shows it — see OrganizationCapabilities.

    // Threads require the VFS-first persistence path; the capability is true only for VFS_FIRST
    // orgs. Defaults to disabled (hidden) until capabilities load — this is the single source of
    // truth for showing thread UI, never URL/localStorage/heuristics.
    const threadsEnabled = computed(
        () => currentOrganizationCapabilities.value?.threads ?? false
    );

    // Business rules are gated by a global env-based feature flag, not persistence mode.
    // Same source of truth (OrganizationCapabilities) as threadsEnabled — defaults to
    // disabled (hidden) until capabilities load.
    const businessRulesEnabled = computed(
        () => currentOrganizationCapabilities.value?.business_rules ?? false
    );

    const userPreferences = computed(() => user.value?.preferences ?? null);

    const hintsEnabled = computed(
        () =>
            user.value !== undefined &&
            (userPreferences.value?.hints_enabled ?? true)
    );

    const seenHints = computed(
        () => (userPreferences.value?.seen_hints ?? []) as string[]
    );

    const setupChecklistPrefs = computed(
        () => userPreferences.value?.setup_checklist ?? {}
    );

    async function patchPreferences(body: {
        hints_enabled?: boolean;
        seen_hints?: string[];
        setup_checklist?: Record<
            string,
            { snoozed_until: string | null; dismissed?: boolean } | null
        >;
    }) {
        const result = await preferencesClient.patch<MeUser>({
            url: "/user/me/preferences",
            body,
            headers: { "Content-Type": "application/json" },
        });
        const data = (result as { data?: MeUser }).data;
        if (data && user.value) {
            user.value.preferences = data.preferences ?? null;
        }
    }

    async function markHintSeen(hintId: string) {
        if (!user.value) return;
        const current = seenHints.value;
        if (current.includes(hintId)) return;
        // Optimistic update so the UI advances immediately without waiting for the network.
        const updated = [...current, hintId];
        user.value.preferences = {
            ...(user.value.preferences ?? {}),
            seen_hints: updated,
        };
        await patchPreferences({ seen_hints: updated });
    }

    async function resetHints() {
        await patchPreferences({ seen_hints: [] });
    }

    async function setHintsEnabled(value: boolean) {
        if (user.value) {
            user.value.preferences = {
                ...(user.value.preferences ?? {}),
                hints_enabled: value,
            };
        }
        await patchPreferences({ hints_enabled: value });
    }

    async function snoozeSetupChecklist(stekId: string) {
        const snoozedUntil = new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000
        ).toISOString();
        await patchPreferences({
            setup_checklist: {
                [stekId]: { snoozed_until: snoozedUntil, dismissed: false },
            },
        });
    }

    async function dismissSetupChecklist(stekId: string) {
        await patchPreferences({
            setup_checklist: {
                [stekId]: { snoozed_until: null, dismissed: true },
            },
        });
    }

    async function undismissSetupChecklist(stekId: string) {
        await patchPreferences({
            setup_checklist: {
                [stekId]: { snoozed_until: null, dismissed: false },
            },
        });
    }

    return {
        user,
        currentOrganizationRole,
        currentOrganizationRoles,
        currentOrganizationPermissions,
        currentOrganizationCapabilities,
        threadsEnabled,
        businessRulesEnabled,
        reset,
        photoUrl: computed(() => user.value?.profile_picture || ""),
        hintsEnabled,
        seenHints,
        markHintSeen,
        resetHints,
        setHintsEnabled,
        setupChecklistPrefs,
        snoozeSetupChecklist,
        dismissSetupChecklist,
        undismissSetupChecklist,
    };
});

export const useUserManager = () => storeToRefs(useUserStore());
